import type { FeatureDto } from '@codeframe/api-contract';
import { Cpu, GraduationCap, LayoutDashboard, Library, Lock, LogOut, Bot, Swords, UserRound } from 'lucide-react';
import type { ReactNode } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import { useLogout, useMe } from '../api/hooks.js';
import { Button, Meter, cx } from './ui.js';
import s from './AppShell.module.css';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  feature?: FeatureDto['id'];
}

const NAV: { section: string; items: NavItem[] }[] = [
  { section: 'Command', items: [{ to: '/', label: 'Hangar', icon: <LayoutDashboard size={18} /> }] },
  {
    section: 'Train',
    items: [{ to: '/academy', label: 'Academy', icon: <GraduationCap size={18} />, feature: 'academy' }],
  },
  {
    section: 'Engineer',
    items: [
      { to: '/workshop', label: 'Workshop', icon: <Cpu size={18} />, feature: 'workshop' },
      { to: '/frame', label: 'Frame', icon: <Bot size={18} /> },
      { to: '/library', label: 'Code Library', icon: <Library size={18} /> },
    ],
  },
  { section: 'Deploy', items: [{ to: '/arena', label: 'Arena', icon: <Swords size={18} />, feature: 'arena' }] },
  { section: 'Pilot', items: [{ to: '/profile', label: 'Profile', icon: <UserRound size={18} /> }] },
];

/** The site icon (`src/icons`), resized to WebP at build time. */
const SITE_ICON = Object.values(
  import.meta.glob<string>('../../../../src/icons/CODEFRAME_SITE_ICON.png', { eager: true, import: 'default', query: { w: '128', format: 'webp' } }),
)[0];

export function BrandMark({ className }: { className?: string }) {
  return <img src={SITE_ICON} className={className} alt="" aria-hidden />;
}

export function AppShell() {
  const { data: me } = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  if (!me) return null;
  const p = me.player;
  const feature = (id?: FeatureDto['id']) => (id ? p.features.find((f) => f.id === id) : undefined);
  const lvl = p.level;
  return (
    <div className={s.shell}>
      <div className={s.brand}>
        <BrandMark className={s.brandMark} />
        <span className={s.brandName}>CODEFRAME</span>
      </div>
      <header className={s.top}>
        <div className={s.status}>
          <span className={s.dot} />
          LINK STABLE · PYTHON RUNTIME ONLINE
        </div>
        <div className={s.pilot}>
          <div className={s.level}>
            <span className={s.levelBadge}>LV {lvl.level}</span>
            <div className={s.xp}>
              <Meter
                value={lvl.xp - lvl.levelStartXp}
                max={lvl.nextLevelXp === null ? 1 : lvl.nextLevelXp - lvl.levelStartXp}
                display={lvl.nextLevelXp === null ? 'MAX' : `${lvl.xp}/${lvl.nextLevelXp} XP`}
              />
            </div>
          </div>
          <span className={s.callsign}>{me.user.username}</span>
          <Button
            variant="ghost"
            size="small"
            icon={<LogOut size={15} />}
            aria-label="Sign out"
            onClick={() => logout.mutate(undefined, { onSuccess: () => navigate('/login') })}
          />
        </div>
      </header>
      <nav className={s.nav} aria-label="Main">
        {NAV.map((group) => (
          <div key={group.section}>
            <div className={cx('label', s.navSection)}>{group.section}</div>
            {group.items.map((item) => {
              const f = feature(item.feature);
              const locked = f && !f.unlocked;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) => cx(s.navLink, isActive && s.navActive, locked && s.navLocked)}
                  title={locked ? `Unlocks at level ${f.minLevel}` : item.label}
                >
                  {item.icon}
                  <span className={s.navLabel}>{item.label}</span>
                  {locked && (
                    <span className={s.navLock}>
                      <Lock size={12} />
                      <span>LV{f.minLevel}</span>
                    </span>
                  )}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>
      <main className={s.main}>
        <Outlet />
      </main>
    </div>
  );
}
